if (!customElements.get('ncm-sticky-atc')) {
  customElements.define(
    'ncm-sticky-atc',
    class NcmStickyAtc extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-sticky-atc-button]');
        this.labelElement = this.querySelector('[data-sticky-atc-label]');
        this.spinner = this.button.querySelector('.loading__spinner');
        this.priceElement = this.querySelector('[data-sticky-atc-price]');
        this.compareElement = this.querySelector('[data-sticky-atc-compare]');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        this.mainInView = false;
        this.awaitingResult = false;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.button.addEventListener('click', this.onClick.bind(this), { signal });
        this.mobileQuery.addEventListener('change', this.updateVisibility.bind(this), { signal });
        document.addEventListener('product-info:loaded', this.bindProduct.bind(this), { signal });
        document.addEventListener(
          'ncm:selection-change',
          (event) => {
            if (event.target !== this.productInfo) return;
            this.updatePrice();
            this.syncButton();
          },
          { signal }
        );

        this.unsubscribers = [
          subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.dataset.sectionId) return;
            this.updatePrice();
            this.syncButton();
          }),
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            this.awaitingResult = false;
          }),
          subscribe(PUB_SUB_EVENTS.cartError, () => {
            if (!this.awaitingResult) return;
            this.awaitingResult = false;
            this.form
              ?.closest('product-form')
              ?.querySelector('.product-form__error-message-wrapper')
              ?.scrollIntoView({ behavior: this.scrollBehavior, block: 'center' });
          }),
        ];

        document.body.classList.add('ncm-sticky-atc-enabled');
        this.bindProduct();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
        if (!document.querySelector('ncm-sticky-atc')) {
          document.body.classList.remove('ncm-sticky-atc-enabled');
        }
      }

      bindProduct() {
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();

        this.productInfo = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        this.form = document.getElementById(this.dataset.productFormId);
        this.mainButton = this.form?.querySelector('button[type="submit"][name="add"]');

        if (this.mainButton) {
          this.intersectionObserver = new IntersectionObserver(
            ([entry]) => {
              this.mainInView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
              this.updateVisibility();
            },
            { rootMargin: '0px 0px -80px 0px', threshold: [0, 0.5, 1] }
          );
          this.intersectionObserver.observe(this.mainButton);

          this.mutationObserver = new MutationObserver(this.syncButton.bind(this));
          this.mutationObserver.observe(this.mainButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
            childList: true,
            characterData: true,
            subtree: true,
          });
        }

        this.updatePrice();
        this.syncButton();
        this.updateVisibility();
      }

      get scrollBehavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      updateVisibility() {
        const visible = Boolean(this.mainButton) && this.mobileQuery.matches && !this.mainInView;
        this.dataset.visible = String(visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.toggleAttribute('inert', !visible);
      }

      updatePrice() {
        const priceContainer = this.productInfo?.querySelector(`#price-${this.dataset.sectionId}`);
        const price = priceContainer?.querySelector('.price');
        if (!price || priceContainer.classList.contains('hidden')) return;

        const onSale = price.classList.contains('price--on-sale');
        const current = price.querySelector(
          onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular'
        );
        const compare = onSale ? price.querySelector('.price__sale s.price-item--regular') : null;

        if (current) this.priceElement.textContent = this.formatPriceText(current.textContent);
        if (this.compareElement) {
          const compareText = compare ? this.formatPriceText(compare.textContent) : '';
          this.compareElement.textContent = compareText;
          this.compareElement.hidden = !compareText;
        }
      }

      formatPriceText(text) {
        const currencyCode = this.dataset.currencyCode;
        let value = text.replace(/\s+/g, ' ').trim();
        if (currencyCode) value = value.replace(currencyCode, '').trim();
        return value;
      }

      // The bar mirrors the main submit button: its label, disabled state and the selection state
      // that product-info keeps (choose size, choose another size/color, ready, sold out).
      syncButton() {
        if (!this.mainButton) return;

        const busy = this.mainButton.classList.contains('loading');
        const selectionState = this.productInfo?.dataset.selectionState;
        const mainLabel = this.mainButton.querySelector('span')?.textContent.trim();
        let state = 'ready';
        let label = this.dataset.label;

        if (this.mainButton.disabled) {
          state = 'disabled';
          if (mainLabel && mainLabel !== window.variantStrings?.addToCart) label = mainLabel;
        } else if (selectionState && selectionState !== 'ready') {
          state = selectionState;
          if (mainLabel) label = mainLabel;
        } else if (!selectionState && this.productInfo?.querySelector('[data-ncm-selection-data]')) {
          // Selection state not initialized yet: keep the server-rendered Choose size label.
          state = this.dataset.state || 'choose-size';
          label = mainLabel || this.labelElement.textContent || label;
        }

        this.dataset.state = state;
        this.labelElement.textContent = label;
        this.button.disabled = state === 'disabled';
        this.button.classList.toggle('loading', busy);
        this.button.setAttribute('aria-disabled', String(busy || state === 'disabled'));
        this.spinner?.classList.toggle('hidden', !busy);
      }

      // The main form decides what a click does: add to cart, or bring the customer to the option to choose.
      onClick() {
        const button = this.mainButton;
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        this.awaitingResult = this.dataset.state === 'ready';
        button.click();
      }
    }
  );
}
